import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, Wallet, UserCircle, LogOut, Menu, X,
  Power, Coffee, Star, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LAWYER_BASE, LAWYER_LOGIN_PATH } from "@lawyer/constants/route";

const NAV = [
  { to: "", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "consultations", label: "Consultations", icon: Calendar },
  { to: "earnings", label: "Earnings", icon: Wallet },
  { to: "reviews", label: "Reviews", icon: Star },
  { to: "kyc", label: "Verification", icon: ShieldCheck },
  { to: "profile", label: "Profile", icon: UserCircle },
];

function SidebarItem({ to, label, icon: Icon, exact }) {
  return (
    <NavLink
      to={`${LAWYER_BASE}/${to}`}
      end={exact}
      data-testid={`lawyer-nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
          isActive
            ? "bg-[#D4AF37] text-[#0A2540] shadow"
            : "text-slate-300 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      <Icon size={18} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function LawyerLayout({ children, available, vacationMode, onAvailabilityToggle, onVacationToggle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const initials = (user?.name || user?.email || "L").split(" ").slice(-2).map((x) => x[0]).join("").toUpperCase();
  const handleLogout = async () => { await logout(); navigate(LAWYER_LOGIN_PATH); };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#0A2540] text-white px-4 py-3 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen(!open)} aria-label="Toggle sidebar" data-testid="lawyer-sidebar-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <span className="font-bold tracking-wide" style={{ fontFamily: "Poppins" }}>Advoset Pro</span>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-bold ${available ? "text-emerald-400" : "text-rose-300"}`}>
          {available ? "Online" : "Offline"}
        </span>
      </header>

      <aside
        data-testid="lawyer-sidebar"
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#0A2540] text-white p-4 z-40 transition-transform lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        } flex flex-col`}
      >
        <div className="hidden lg:flex items-center gap-2 px-2 py-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-[#D4AF37] grid place-items-center text-[#0A2540] font-extrabold">A</div>
          <div className="leading-tight">
            <p className="font-extrabold tracking-tight" style={{ fontFamily: "Poppins" }}>Advoset Pro</p>
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Legal Help, Simplified</p>
          </div>
        </div>

        {/* Status pill in the sidebar */}
        <div className="px-2 mb-3" data-testid="lawyer-status-card">
          <div className={`rounded-xl border p-3 ${vacationMode ? "border-amber-300 bg-amber-500/10" : available ? "border-emerald-400 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-300">Status</p>
            <p className={`mt-0.5 text-sm font-extrabold ${vacationMode ? "text-amber-300" : available ? "text-emerald-300" : "text-slate-300"}`}>
              {vacationMode ? "On vacation" : available ? "Online — accepting bookings" : "Offline"}
            </p>
            <div className="mt-2 flex gap-2">
              <button
                onClick={onAvailabilityToggle}
                disabled={vacationMode}
                data-testid="toggle-online"
                className={`flex-1 text-[11px] font-bold px-2 py-1 rounded-md border ${available ? "border-rose-300 text-rose-200" : "border-emerald-300 text-emerald-200"} disabled:opacity-40`}
              >
                <Power size={10} className="inline mr-1" />
                {available ? "Go offline" : "Go online"}
              </button>
              <button
                onClick={onVacationToggle}
                data-testid="toggle-vacation"
                className={`flex-1 text-[11px] font-bold px-2 py-1 rounded-md border ${vacationMode ? "border-amber-300 text-amber-200" : "border-white/20 text-slate-200"}`}
              >
                <Coffee size={10} className="inline mr-1" />
                {vacationMode ? "End break" : "Vacation"}
              </button>
            </div>
          </div>
        </div>

        <nav className="space-y-1 flex-1">
          {NAV.map((item) => <SidebarItem key={item.label} {...item} />)}
        </nav>

        <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[#D4AF37] text-[#0A2540] grid place-items-center font-extrabold text-sm">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate" data-testid="lawyer-current-user-name">{user?.name || user?.email}</p>
            <p className="text-[10px] text-slate-400 truncate">Lawyer</p>
          </div>
          <button onClick={handleLogout} className="text-slate-300 hover:text-white" title="Sign out" data-testid="lawyer-logout-btn">
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {open && <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto" data-testid="lawyer-main">
          {children}
        </div>
      </main>
    </div>
  );
}
